import { supabase } from '@/integrations/supabase/client';
import { AppState } from '@/types';
import { saveHabitToSupabase, saveDailyToSupabase, saveTodoToSupabase } from '@/utils/supabaseHabitSaver';

export async function saveUserDataToSupabase(
  userId: string,
  newState: AppState,
  options?: { strategy?: 'safe-merge' | 'prefer-app' }
): Promise<void> {
  // CRITICAL: Don't save during data clearing operations
  if ((window as any).clearingData) {
    console.log('🛑 Skipping save during data clear operation');
    return;
  }
  try {
    console.log('💾 Saving user data:', { 
      userId, 
      totalPoints: newState.totalPoints, 
      currentWeekPoints: newState.currentWeekPoints,
      activitiesCount: newState.activities?.length || 0,
      lastStreakDate: newState.lastStreakDate,
      streak: newState.streak,
      stateDebug: {
        isAuthenticated: newState.isAuthenticated,
        user: newState.user?.id
      }
    });

    // CRITICAL FIX: Get current DB stats to avoid overwriting with stale data
    const { data: currentDbStats, error: fetchError } = await supabase
      .from('user_stats')
      .select('total_points, current_week_points, activity_credits, awarded_flexi_credits, activities_logged_today, last_daily_reset, last_streak_date, experience')
      .eq('user_id', userId)
      .single();

    if (fetchError) {
      console.error('❌ Error fetching current DB stats:', fetchError);
      throw fetchError;
    }

    console.log('📊 Current DB stats vs App state:', {
      db: currentDbStats,
      app: { 
        total_points: newState.totalPoints, 
        current_week_points: newState.currentWeekPoints,
        activity_credits: newState.activityCredits,
        activities_logged_today: newState.activitiesLoggedToday
      }
    });

// Detect if this is a reset operation (both current_week_points AND activities_logged_today going to 0)
const isResetOperation = newState.currentWeekPoints === 0 && newState.activitiesLoggedToday === 0;

const strategy = options?.strategy ?? 'safe-merge';

console.log('💾 Data saver - reset detection:', {
  appCurrentWeekPoints: newState.currentWeekPoints,
  appActivitiesLoggedToday: newState.activitiesLoggedToday,
  isResetOperation,
  strategy
});

// Helper: choose value based on strategy
const pick = (dbVal: number | null | undefined, appVal: number | null | undefined) => {
  const db = Number(dbVal || 0);
  const app = Number(appVal || 0);
  return strategy === 'prefer-app' ? app : Math.max(db, app);
};

// Use chosen strategy to build final stats
const finalTotalPoints = pick(currentDbStats.total_points, newState.totalPoints);
const finalExperience = strategy === 'prefer-app' ? (newState.experience || 0) : Math.max(currentDbStats.experience || 0, newState.experience || 0);
const finalActivityCredits = Math.round((strategy === 'prefer-app' ? (newState.activityCredits || 0) : Math.max(Number(currentDbStats.activity_credits || 0), Number(newState.activityCredits || 0))) * 10) / 10;
const finalCurrentWeekPoints = Math.round(strategy === 'prefer-app' ? newState.currentWeekPoints : (isResetOperation ? newState.currentWeekPoints : Math.max(currentDbStats.current_week_points, newState.currentWeekPoints)));
const finalActivitiesLoggedToday = strategy === 'prefer-app' ? newState.activitiesLoggedToday : (isResetOperation ? newState.activitiesLoggedToday : Math.max(currentDbStats.activities_logged_today, newState.activitiesLoggedToday));

const finalStats = {
  total_points: finalTotalPoints,
  experience: finalExperience,
  activity_credits: finalActivityCredits,
  current_week_points: finalCurrentWeekPoints,
  streak: newState.streak,
  last_streak_date: newState.lastStreakDate,
  activities_logged_today: finalActivitiesLoggedToday,
  activity_counts: newState.activityCounts,
  badges: newState.badges.map(badge => 
    typeof badge === 'string' ? badge : JSON.stringify({
      id: badge.id,
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      rarity: badge.rarity,
      unlockedAt: badge.unlockedAt
    })
  ),
  weekly_start_date: newState.weeklyStartDate.toISOString(),
  weekly_goal_streak: newState.weeklyGoalStreak,
  last_week_goal_achieved: newState.lastWeekGoalAchieved,
  weekly_bonus_earned: newState.weeklyBonusEarned,
  weekly_bonus_awarded: newState.weeklyBonusAwarded,
  last_login_date: newState.lastLoginDate?.toISOString(),
  last_daily_reset: newState.lastDailyReset,
  updated_at: new Date().toISOString()
};

    console.log('🎯 STREAK SAVE DEBUG: Saving streak:', finalStats.streak, 'lastStreakDate:', finalStats.last_streak_date);

    console.log('📊 Final stats being saved:', finalStats);

    const { error: statsError } = await supabase
      .from('user_stats')
      .update(finalStats)
      .eq('user_id', userId);

    if (statsError) {
      console.error('❌ Error saving user stats:', statsError);
      throw statsError;
    } else {
      console.log('✅ Successfully saved user stats');
    }

    // Update user profile - save weekly_goal even if user object is incomplete
    const weeklyGoalToSave = newState.user?.weeklyGoal ?? newState.weeklyGoal;
    
    if (newState.user || weeklyGoalToSave !== undefined) {
      console.log('💾 Saving profile data:', {
        userId,
        name: newState.user?.name,
        profile_picture: newState.user?.profilePicture,
        weekly_goal: weeklyGoalToSave
      });
      
      // Build update object with only defined values
      const updateData: Record<string, any> = {};
      if (newState.user?.name) updateData.name = newState.user.name;
      if (newState.user?.profilePicture !== undefined) updateData.profile_picture = newState.user.profilePicture;
      if (weeklyGoalToSave !== undefined) updateData.weekly_goal = weeklyGoalToSave;
      
      if (Object.keys(updateData).length > 0) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('user_id', userId);

        if (profileError) {
          console.error('❌ Error saving user profile:', profileError);
          throw profileError;
        } else {
          console.log('✅ Successfully saved user profile');
        }
      }
    }

    // Update simple pet if it exists
    if (newState.simplePet) {
      console.log('💾 Saving simple pet:', {
        userId,
        petName: newState.simplePet.name,
        stage: newState.simplePet.stage,
        happiness: newState.simplePet.happiness,
        health: newState.simplePet.health,
        totalPoints: newState.simplePet.totalPoints
      });
      
      try {
        const { error: simplePetError } = await supabase
          .from('simple_pets')
          .upsert({
            user_id: userId,
            name: newState.simplePet.name,
            emoji: newState.simplePet.emoji,
            stage: newState.simplePet.stage,
            happiness: newState.simplePet.happiness,
            health: newState.simplePet.health,
            total_points: newState.simplePet.totalPoints,
            last_interaction: newState.simplePet.lastInteraction.toISOString(),
            pledge_sheet_id: newState.simplePet.pledgeSheetId,
            daily_target_progress: newState.simplePet.dailyTargetProgress,
            weekly_pledge_progress: newState.simplePet.weeklyPledgeProgress,
            last_daily_update: newState.simplePet.lastDailyUpdate.toISOString(),
            last_weekly_update: newState.simplePet.lastWeeklyUpdate.toISOString(),
            pledge_performance_streak: newState.simplePet.pledgePerformanceStreak
          }, {
            onConflict: 'user_id',
            ignoreDuplicates: false
          });
        
        if (simplePetError) {
          console.error('❌ Error saving simple pet:', simplePetError);
          throw simplePetError;
        } else {
          console.log('✅ Successfully saved simple pet');
        }
      } catch (error) {
        console.error('❌ Failed to save simple pet:', error);
        throw error;
      }
    }

    // Update virtual pet if it exists
    if (newState.virtualPet) {
      console.log('💾 Saving virtual pet:', {
        userId,
        petId: newState.virtualPet.id,
        name: newState.virtualPet.name,
        species: newState.virtualPet.species,
        stage: newState.virtualPet.stage
      });
      
      try {
        const { error: petError } = await supabase
          .from('virtual_pets')
          .upsert({
            user_id: userId,
            name: newState.virtualPet.name,
            species: newState.virtualPet.species,
            stage: newState.virtualPet.stage,
            happiness: newState.virtualPet.happiness,
            hunger: newState.virtualPet.hunger,
            energy: newState.virtualPet.energy,
            health: newState.virtualPet.health,
            available_portions: newState.virtualPet.availablePortions,
            portion_timestamps: newState.virtualPet.portionTimestamps?.map(ts => ts.toISOString()) || [],
            unlocked_items: newState.virtualPet.unlockedItems,
            current_outfit: newState.virtualPet.currentOutfit,
            evolution_points: newState.virtualPet.evolutionPoints,
            personality_traits: newState.virtualPet.personalityTraits,
            social_stats: newState.virtualPet.socialStats || {
              battlesWon: 0,
              giftsGiven: 0,
              battlesLost: 0,
              friendships: [],
              socialScore: 0,
              giftsReceived: 0,
              playdatesCompleted: 0
            },
            last_fed: newState.virtualPet.lastFed.toISOString(),
            last_played: newState.virtualPet.lastPlayed.toISOString(),
            last_checked: newState.virtualPet.lastChecked.toISOString()
          }, {
            onConflict: 'user_id',
            ignoreDuplicates: false
          });
        
        if (petError) {
          console.error('❌ Error saving virtual pet:', petError);
          throw petError;
        } else {
          console.log('✅ Successfully saved virtual pet');
        }
      } catch (error) {
        console.error('❌ Failed to save virtual pet:', error);
        throw error;
      }
    }

    // Save habits, dailies, and todos
    if (newState.habits && newState.habits.length > 0) {
      console.log('💾 Saving habits/dailies/todos - types found:', newState.habits.map(h => h.type));
      
      const habitPromises = newState.habits.map(async (habit) => {
        try {
          // Check for actual habit types (positive/negative)
          // MIGRATION: Convert old 'habit' type to 'positive'
          if (habit.type === 'habit') {
            habit.type = 'positive';
            console.log('🔄 Migrated habit type from "habit" to "positive" for:', habit.name);
          }
          
          if (habit.type === 'positive' || habit.type === 'negative' || habit.type === 'neutral') {
            return await saveHabitToSupabase(habit);
          } else if (habit.type === 'daily') {
            return await saveDailyToSupabase(habit);
          } else if (habit.type === 'todo') {
            return await saveTodoToSupabase(habit);
          }
          
          console.warn(`Unknown habit type: ${habit.type} for ${habit.name}`);
          return true;
        } catch (error) {
          console.error(`Failed to save ${habit.type} ${habit.name}:`, error);
          return false;
        }
      });
      
      await Promise.all(habitPromises);
    }
  } catch (error) {
    console.error('Error saving user data:', error);
    throw error;
  }
}

export async function saveActivityToSupabase(activity: any): Promise<void> {
  try {
    console.log('💾 SAVE ACTIVITY TO DB - Starting:', { 
      activityId: activity.id, 
      points: activity.points, 
      userId: activity.userId 
    });

    // Ensure we have a valid UUID for the activity
    const activityId = activity.id && activity.id.length > 10 ? activity.id : crypto.randomUUID();
    
    const { error } = await supabase
      .from('activities')
      .upsert({
        id: activityId,
        user_id: activity.userId,
        name: activity.name,
        points: activity.points,
        timestamp: activity.timestamp.toISOString(),
        description: activity.description,
        duration: activity.duration,
        notes: activity.notes,
        photo_url: activity.photoUrl,
        likes: activity.likes || [],
        latitude: activity.latitude,
        longitude: activity.longitude,
        location_accuracy: activity.locationAccuracy,
        location_name: activity.locationName,
        policy_name: activity.policyName,
        annualized_premium: activity.annualizedPremium
      }, { onConflict: 'id' });

    if (error) {
      console.error('❌ Error saving activity to activities table:', error);
      throw error;
    }

    console.log('✅ Activity saved to activities table successfully');
  } catch (error) {
    console.error('❌ Failed to save activity to database:', error);
    throw error;
  }
}

export async function saveActivityCommentToSupabase(activityId: string, comment: any): Promise<void> {
  try {
    // Always generate a new UUID for database insertion to avoid conflicts
    const commentId = crypto.randomUUID();
    
    const { error } = await supabase
      .from('activity_comments')
      .insert({
        id: commentId,
        activity_id: activityId,
        user_id: comment.userId,
        content: comment.content,
        timestamp: comment.timestamp.toISOString()
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error saving comment:', error);
    throw error;
  }
}

export async function updateActivityLikesInSupabase(activityId: string, likes: string[]): Promise<void> {
  try {
    const { error } = await supabase
      .from('activities')
      .update({ likes })
      .eq('id', activityId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating activity likes:', error);
    throw error;
  }
}

export async function deleteActivityFromSupabase(activityId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('activities')
      .delete()
      .eq('id', activityId);

    if (error) throw error;
    console.log('✅ Activity deleted from database:', activityId);
  } catch (error) {
    console.error('❌ Error deleting activity:', error);
    throw error;
  }
}